import React, { useEffect, useState, useMemo, useCallback } from "react";
import { EcommProductPrice } from "@arctheme-components/ecommerce/Price/Price";
import ImageComponent from "@project-stories/elements/Image/Image";
import ProductTileQuickAdd from "./ProductTileQuickAdd/ProductTileQuickAdd";
import { clsx } from "clsx";
import { getAlpineStore } from "../../../../entrypoints/theme";
import shopify from "../../../../js/shopify";

// TypeScript interfaces
interface ProductImage {
  src: string;
  alt?: string;
}

interface ProductSwatch {
  url: string;
  name: string;
  image?: string;
  color?: string;
  is_product: boolean;
  show_name?: boolean;
}

interface ProductTileProps {
  product: any;
  swatches?: any[];
  siblings?: any[];
  related_products?: any[];
  show_add?: boolean;
  show_price_inline?: boolean;
}

// Utility functions
const isSuitKit = (product: any): boolean => {
  return product?.type === 'Kit' || 
    product?.productType === 'Kit' ||
    product?.tags?.some((tag: string) => tag.includes('isSuitKit')) ||
    product?.template_suffix === 'suit-kit';
};

const getSuitComponents = (metafields: any) => [
  { key: "matching_jacket", label: "Jacket", product: metafields?.matching_jacket || metafields?.ap21?.matchingJacket?.value },
  { key: "matching_vest", label: "Waistcoat", product: metafields?.matching_vest || metafields?.ap21?.matchingVest?.value },
  { key: "matching_trouser", label: "Trousers", product: metafields?.matching_trouser || metafields?.ap21?.matchingTrouser?.value }
];

const calculatePriceFromProducts = (products: any[]): number => {
  return products.reduce((total, { product: refProduct }) => {
    if (!refProduct) return total;
    
    if (refProduct.price) {
      return total + (refProduct.price / 100);
    }
    if (refProduct.priceRange?.minVariantPrice?.amount) {
      return total + parseFloat(refProduct.priceRange.minVariantPrice.amount);
    }
    return total;
  }, 0);
};

const getHandlesFromProducts = (products: any[]): string[] => {
  return products
    .map(({ product: refProduct }) => refProduct?.handle)
    .filter(Boolean);
};

const createKeyValuePairs = (tags: string | string[]) => {
  const tagsArray = Array.isArray(tags) ? tags : [tags];
  return tagsArray.reduce((acc, tag) => {
    const [key, value] = tag.split(":");
    if (value) {
      if (acc[key]) {
        acc[key] = Array.isArray(acc[key]) ? [...acc[key], value] : [acc[key], value];
      } else {
        acc[key] = value;
      }
    }
    return acc;
  }, {} as Record<string, string | string[]>);
};

// Custom hooks
const useSuitPrice = (product: any) => {
  const [suitPrice, setSuitPrice] = useState<number | null>(null);

  const fetchProductPrices = useCallback(async (handles: string[]): Promise<number> => {
    let totalPrice = 0;
    
    const requests = handles.map(async (handle) => {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (response.ok) {
          const data = await response.json();
          return data?.variants?.[0]?.price || 0;
        }
      } catch (error) {
        console.warn(`Failed to fetch product data for handle ${handle}:`, error);
      }
      return 0;
    });

    const prices = await Promise.all(requests);
    return prices.reduce((sum, price) => sum + price, 0) / 100;
  }, []);

  const calculateSuitPrice = useCallback(async (metafields: any) => {
    const suitComponents = getSuitComponents(metafields);
    
    // Try to calculate from direct product references
    const directPrice = calculatePriceFromProducts(suitComponents);
    if (directPrice > 0) {
      return directPrice;
    }

    // Fallback to API calls using handles
    const handles = getHandlesFromProducts(suitComponents);
    if (handles.length > 0) {
      return await fetchProductPrices(handles);
    }

    return 0;
  }, [fetchProductPrices]);

  useEffect(() => {
    const fetchSuitPrice = async () => {
      if (!isSuitKit(product)) {
        return;
      }

      try {
        let totalPrice = 0;

        // First try with existing metafields
        if (product.metafields) {
          totalPrice = await calculateSuitPrice(product.metafields);
        }

        // If no price found and we have a product ID, fetch full product data
        if (totalPrice === 0 && product.id) {
          const productList = await shopify.getShopifyProductList([product.id]);
          if (productList?.[0]?.metafields) {
            totalPrice = await calculateSuitPrice(productList[0].metafields);
          }
        }

        if (totalPrice > 0) {
          setSuitPrice(totalPrice);
        }
      } catch (error) {
        console.error('Error calculating suit price:', error);
      }
    };

    fetchSuitPrice();
  }, [product, calculateSuitPrice]);

  return suitPrice;
};

const useProductSwatches = (product: any, siblings: any[], relatedProducts: any[]) => {
  return useMemo(() => {
    const productTags = createKeyValuePairs(product.tags || []);
    const swatches: ProductSwatch[] = [];
    
    if (!productTags?.colour) return swatches;

    const colors = Array.isArray(productTags.colour) ? productTags.colour : [productTags.colour];
    const currentProductHandle = product.handle;

    // Process siblings
    colors.forEach((colorHandle) => {
      const siblingProduct = siblings?.find((sibling) => sibling.handle === colorHandle);
      if (siblingProduct) {
        swatches.push({
          url: `/products/${siblingProduct.handle}`,
          name: product.title,
          image: siblingProduct.images?.find((image: any) => image.src?.match(/_s_|_s(\.|$)/))?.src ||
                 siblingProduct.images?.[siblingProduct.images?.length - 1]?.src,
          is_product: siblingProduct.handle === currentProductHandle,
        });
      }
    });

    // Process related products
    colors.forEach((colorHandle) => {
      const relatedProduct = relatedProducts?.find((related) => related.handle === colorHandle);
      if (relatedProduct && !swatches.some(s => s.url.includes(relatedProduct.handle))) {
        // console.log("related product for colorHandle", colorHandle, relatedProduct.images);
        swatches.push({
          url: `/products/${relatedProduct.handle}`,
          name: product.title,
          image: relatedProduct.images?.edges?.find((edge: any) => 
                   edge.node.src?.match(/_s_|_s(\.|$)/))?.node.src ||
                 relatedProduct.images?.edges?.[relatedProduct.images.edges.length - 1]?.node.src,
          is_product: relatedProduct.handle === currentProductHandle,
        });
      }
    });

    return swatches;
  }, [product, siblings, relatedProducts]);
};

export const ProductTile: React.FC<ProductTileProps> = ({
  product,
  swatches = [],
  siblings = [],
  related_products = [],
  show_add = false,
  show_price_inline = false,
}) => {
  // console.log('ProductTile component mounted!', { 
  //   productTitle: product?.title, 
  //   hasRelatedProducts: !!related_products?.length,
  //   relatedProductsCount: related_products?.length,
  //   swatchesCount: swatches?.length,
  //   siblingsCount: siblings?.length
  // });
  
  const store = getAlpineStore("modelview");
  const [modelView, setModelView] = useState(store.isModelView);
  const [isHovered, setIsHovered] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState(related_products || []);
  const [selectedVariantImages, setSelectedVariantImages] = useState<ProductImage[]>([]);
  const [currentSwatchProduct, setCurrentSwatchProduct] = useState(null);
  const [selectedProductData, setSelectedProductData] = useState<any>(null);
  
  const suitPrice = useSuitPrice(product);
  const productSwatches = useProductSwatches(product, siblings, relatedProducts);

  // Debug: Log props and swatch generation
  // useEffect(() => {
  //   console.log('ProductTile Debug - Props received:', { 
  //     productHandle: product?.handle, 
  //     siblingsCount: siblings?.length, 
  //     relatedProductsCount: relatedProducts?.length,
  //     productSwatchesCount: productSwatches?.length 
  //   });
  //   if (productSwatches?.length > 0) {
  //     console.log('Generated swatches:', productSwatches);
  //   }
  // }, [product, siblings, relatedProducts, productSwatches]);

  // Handle Algolia click tracking
  const handleProductClick = useCallback(() => {
    const windowWithAlgolia = window as any;
    
    // Use current product data (selected or original)
    const trackingProduct = selectedProductData || product;
    
    // Use Algolia objectID for search results, or variant ID as fallback (since Algolia uses variant IDs)
    // Your Algolia index uses variant IDs as objectIDs
    const objectID = trackingProduct.objectID || 
                    (trackingProduct.variants?.[0]?.id?.toString()) || 
                    trackingProduct.id?.toString();
    
    if (!objectID) {
      console.warn('No valid objectID, variant ID, or product ID found for Algolia tracking');
      return;
    }

    // debugging - enabled for flow tracking
    console.log('Algolia click tracking objectID:', {
      objectID,
      source: trackingProduct.objectID ? 'algolia-search' : 
              trackingProduct.variants?.[0]?.id ? 'variant-fallback' : 'product-fallback',
      productId: trackingProduct.id,
      variantId: trackingProduct.variants?.[0]?.id,
      hasAlgoliaObjectID: !!trackingProduct.objectID,
      productTitle: trackingProduct.title,
      isSelectedVariant: !!selectedProductData
    });

    // Try new Alpine.js component method first
    if (windowWithAlgolia.Alpine?.store('algolia')?.handleProductClick) {
      try {
        windowWithAlgolia.Alpine.store('algolia').handleProductClick({
          objectID,
          title: trackingProduct.title,
          url: `/products/${trackingProduct.handle}`,
          position: trackingProduct.position || 1,
          source: trackingProduct.objectID ? 'algolia-search' : 'shopify-direct'
        });
        return;
      } catch (error) {
        console.warn('Alpine Algolia click tracking failed:', error);
      }
    }

    // Fallback to legacy method
    if (windowWithAlgolia.algoliaProductClick) {
      try {
        // Get current queryID with proper validation
        let queryID: string | null = null;
        
        // Try multiple sources for queryID
        if (windowWithAlgolia.algoliaQueryID) {
          queryID = windowWithAlgolia.algoliaQueryID;
        } else {
          const storedQueryID = sessionStorage.getItem('algolia_queryID');
          const storedTimestamp = sessionStorage.getItem('algolia_queryID_timestamp');
          const thirtyMinutes = 30 * 60 * 1000;
          
          if (storedQueryID && storedTimestamp) {
            const age = Date.now() - parseInt(storedTimestamp);
            if (age < thirtyMinutes) {
              queryID = storedQueryID;
            }
          }
        }
          
        windowWithAlgolia.algoliaProductClick(
          objectID,
          trackingProduct.title,
          `/products/${trackingProduct.handle}`,
          queryID
        );

        // debugging - enabled for flow tracking
        console.log('Algolia product click tracked:', {
          objectID,
          queryID,
          hasQueryID: !!queryID,
          source: trackingProduct.objectID ? 'algolia-search' : 'shopify-direct',
          productTitle: trackingProduct.title,
          isSelectedVariant: !!selectedProductData,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.warn('Legacy Algolia click tracking failed:', error);
      }
    }

    // Dispatch custom event for algolia.js component
    try {
      window.dispatchEvent(new CustomEvent('algolia:productClick', {
        detail: {
          objectID,
          title: trackingProduct.title,
          url: `/products/${trackingProduct.handle}`,
          position: trackingProduct.position || 1,
          product: trackingProduct,
          source: trackingProduct.objectID ? 'algolia-search' : 'shopify-direct',
          hasAlgoliaObjectID: !!trackingProduct.objectID,
          isSelectedVariant: !!selectedProductData
        }
      }));
    } catch (error) {
      console.warn('Failed to dispatch algolia:productClick event:', error);
    }
  }, [product, selectedProductData]);

  useEffect(() => {
    const fetchProductSwatches = async () => {
      const relatedProductsShopify = await shopify.getRelatedProducts([product]);
      setRelatedProducts(relatedProductsShopify);
    };

    fetchProductSwatches();

    const handleModelViewToggle = (event: CustomEvent) => {
      setModelView(event.detail.isModelView);
    };

    window.addEventListener("modelview-toggled", handleModelViewToggle as EventListener);

    // Add additional event handling to ensure swatches work
    const handleDocumentClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target && target.closest && target.closest('[data-swatch-handle]')) {
        console.log('Document click intercepted on swatch');
        // The React handler should handle this
      }
    };

    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      window.removeEventListener("modelview-toggled", handleModelViewToggle as EventListener);
      document.removeEventListener('click', handleDocumentClick, true);
      // Reset swatch states on unmount
      setSelectedVariantImages([]);
      setCurrentSwatchProduct(null);
      setSelectedProductData(null);
    };
  }, [product]);

  // Fetch complete product data for swatch functionality (images, price, etc.)
  const fetchRelatedProductData = useCallback(async (productHandle: string) => {
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (response.ok) {
        const productData = await response.json();
        let images = productData.media || productData.images || [];
        
        // Filter out swatch images (images ending with _s)
        images = images.filter((image: any) => 
          image.src && !image.src.match(/_s_|_s(\.|$)/i)
        );

        // Fallback to featured image if no images
        if (!images.length && productData.featured_image) {
          images = [{ src: productData.featured_image }];
        }

        return {
          images,
          price: productData.price,
          compare_at_price: productData.compare_at_price,
          title: productData.title,
          handle: productData.handle,
          id: productData.id,
          variants: productData.variants,
          tags: productData.tags,
          type: productData.type,
          vendor: productData.vendor
        };
      }
    } catch (error) {
      console.warn(`Failed to fetch product data for ${productHandle}:`, error);
    }
    return null;
  }, []);

  // Handle swatch click - update product data immediately (images, price, etc.)
  const handleSwatchClick = useCallback(async (e: React.MouseEvent, swatch: ProductSwatch) => {
    
    // Prevent all default behaviors and event bubbling
    e.preventDefault();
    e.stopPropagation();
    
    const productHandle = swatch.url.split('/').pop()?.split('?')[0];
    
    if (productHandle && productHandle !== product.handle) {
      // Update to different product variant
      setCurrentSwatchProduct(productHandle); // Set immediately for visual feedback
      
      try {
        const productData = await fetchRelatedProductData(productHandle);
        if (productData && productData.images.length > 0) {
          console.log('Product data loaded, updating display:', {
            handle: productData.handle,
            price: productData.price,
            imagesCount: productData.images.length
          });
          setSelectedVariantImages(productData.images);
          setSelectedProductData(productData);
        } else {
          console.warn('No product data found for:', productHandle);
          setCurrentSwatchProduct(null);
          setSelectedProductData(null);
        }
      } catch (error) {
        console.warn(`Failed to load product data for ${productHandle}:`, error);
        setCurrentSwatchProduct(null);
        setSelectedProductData(null);
      }
    } else {
      // Clicking on current product swatch - reset to original
      console.log('Resetting to original product data');
      setSelectedVariantImages([]);
      setCurrentSwatchProduct(null);
      setSelectedProductData(null);
    }
    
    return false;
  }, [product.handle, fetchRelatedProductData]);

  // Handle swatch hover - no preview, just visual feedback
  const handleSwatchHover = useCallback(() => {
    // No image preview on hover - just CSS hover effects
  }, []);

  // Handle swatch mouse leave
  const handleSwatchLeave = useCallback(() => {
    // No action needed since we removed hover preview
  }, []);

  // Process product images - use selected variant images if available
  const processedImages = useMemo(() => {
    // Use selected variant images if a swatch is clicked
    if (selectedVariantImages.length > 0) {
      return selectedVariantImages;
    }

    // Default to original product images (no hover preview)
    let images = product.media || product.images || [];
    
    // Filter out swatch images
    images = images.filter((image: any) => 
      image.src && !image.src.match(/_s_|_s(\.|$)/i)
    );

    // Fallback to featured image if no images
    if (!images.length && product.featured_image) {
      images = [{ src: product.featured_image }];
    }

    return images;
  }, [product, selectedVariantImages]);

  // Calculate image indices based on model view and swatch detection
  const { imageIndex, hoverIndex } = useMemo(() => {
    const secondImageIsSwatch = /_s$/i.test(processedImages?.[1]?.src);
    
    if (modelView) {
      if (processedImages?.length > 1 && !secondImageIsSwatch) {
        return { imageIndex: 1, hoverIndex: 0 };
      }
      return { imageIndex: 0, hoverIndex: 0 };
    }
    
    return { 
      imageIndex: 0, 
      hoverIndex: processedImages?.length > 1 ? 1 : 0 
    };
  }, [modelView, processedImages]);

  // Calculate final product price - use selected product data if available
  const currentProduct = selectedProductData || product;
  const productPrice = suitPrice !== null ? suitPrice : (currentProduct?.price / 100 || 0);
  const comparePrice = currentProduct?.compare_at_price / 100;

  // Extract product tags and group fit - use selected product data if available
  const productTags = useMemo(() => createKeyValuePairs(currentProduct.tags || []), [currentProduct.tags]);
  const groupFit = productTags?.Group_Fit || productTags?.group_fit;

  // Get currency symbol
  const currencySymbol = (window as any).Alpine?.store('currency')?.getCurrencySymbol() || '$';

  // Get the current product URL (original or selected variant)
  const currentProductUrl = useMemo(() => {
    if (currentSwatchProduct) {
      return `/products/${currentSwatchProduct}`;
    }
    return `/products/${product.handle}`;
  }, [currentSwatchProduct, product.handle]);

  // Utility function for image URLs
  const getImageUrl = (image: any) => {
    return typeof image === "object" && image?.src ? image.src : image;
  };

  return (
    <div 
      className={"theme__product-card"}
      data-product-id={product.objectID || product.id}
    >
      <div className={"productcard__content"}>
        <a
          href={currentProductUrl}
          className="relative w-full productcard__content__image"
          onMouseEnter={() => {
            setIsHovered(true);
          }}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleProductClick}
        >
          <ImageComponent
            classes={clsx(
              "product-image transition-opacity duration-300",
              isHovered &&
                processedImages.length > 1 &&
                "opacity-0",
            )}
            imageUrl={getImageUrl(processedImages[imageIndex])}
          />
          <ImageComponent
            classes={clsx(
              "product-image absolute top-0 left-0 transition-opacity duration-300",
              isHovered && processedImages.length > 1
                ? "opacity-100"
                : "opacity-0",
            )}
            imageUrl={getImageUrl(processedImages[hoverIndex])}
          />
          
          {/* Quick Add Component */}
          {show_add && (
            <ProductTileQuickAdd product={product} />
          )}
        </a>

        {/* Product Swatches - Outside of main product link */}
        {productSwatches.length > 0 && (
          <div className="product__swatches mt-[16px]" data-react-swatches="true">
            {productSwatches.map((swatch, index) => {
              const swatchProductHandle = swatch.url.split('/').pop()?.split('?')[0];
              const isCurrentlySelected = currentSwatchProduct === swatchProductHandle || 
                                        (swatch.is_product && !currentSwatchProduct);
              
              return (
                <div
                  key={`${swatch.url}-${index}`}
                  className={clsx("inline-block", {
                    "border-b-2 border-[#54565A]": isCurrentlySelected,
                  })}
                >
                  <button
                    type="button"
                    className="product__swatch h-[16px] w-[16px] mb-[2px] cursor-pointer transition-all duration-200 hover:opacity-70 hover:scale-110 relative z-10 border-none bg-transparent p-0"
                    onClick={(e) => handleSwatchClick(e, swatch)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onMouseEnter={() => handleSwatchHover(swatch)}
                    onMouseLeave={handleSwatchLeave}
                    title={`View ${swatch.name || 'variant'}`}
                    style={{ pointerEvents: 'auto' }}
                    data-swatch-handle={swatchProductHandle}
                  >
                    {swatch.color && (
                      <div className="mb-[2px] pointer-events-none">
                        <div
                          className="pointer-events-none product__swatch_color"
                          style={{ backgroundColor: swatch.color }}
                        />
                      </div>
                    )}
                    {swatch.image && (
                      <div className="h-[16px] w-[16px] mb-[2px] cursor-pointer pointer-events-none">
                        <img
                          className="object-cover w-full h-full pointer-events-none product__swatch_image"
                          src={swatch.image}
                          alt={swatch.name}
                          loading="lazy"
                        />
                      </div>
                    )}
                    {swatch.show_name && swatch.name}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className={"flex justify-between mt-[16px] flex-col lg:flex-row"}>
          <div>
            <a href={currentProductUrl} onClick={handleProductClick}>
              <p className={"b2 text-[#54565A]"}>{currentProduct?.title}</p>
            </a>
            <p className={"bc uppercase text-[13px] font-[500] text-[#54565A]"}>
              {groupFit}
            </p>
          </div>
          <div
            className={
              "flex items-center justify-end flex-row-reverse lg:flex-col lg:items-end"
            }
          >
            {!show_price_inline && (
              <span className="b1 font-regular text-[#54565a]">
                <EcommProductPrice
                  price={`${currencySymbol}${productPrice.toFixed(2)}`}
                  compare_at_price={comparePrice}
                />
              </span>
            )}
            {comparePrice > 0 && productPrice < comparePrice && (
              <span className="text-[12px] line-through text-[#BBBBBD] mr-[8px] lg:mr-0">
                <div className="product__price" data-prodify-price-container="">
                  {`${currencySymbol}${comparePrice.toFixed(2)}`}
                </div>
              </span>
            )}
          </div>
        </div>

        <div className="mt-[16px]">
          {productTags.automatic && (
            <p className="b3 text-[14px] font-[400] text-[#CDA176]">
              {productTags.automatic}
            </p>
          )}
          {productTags.preorder && (
            <p className="bg-[#54565a] text-white px-3 py-1 text-xs font-semibold inline-block uppercase">
              PRE-ORDER
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductTile;